/**
 * Error normalization for the single HTTP layer (03-02 §5).
 *
 * Every failed request — an axios transport/HTTP error, or a `{ success: false }`
 * envelope — is funnelled into one `NormalizedApiError` shape so consumers never
 * branch on axios internals. `getErrorMessage` maps any thrown value to a stable
 * i18n message key; `@smartsash/i18n` owns the key → copy translation.
 */

/** Stable message keys consumers translate. Kept here so the mapping has one home. */
export const ERROR_MESSAGE_KEYS = {
  network: 'errors.network',
  timeout: 'errors.timeout',
  unauthorized: 'errors.unauthorized',
  forbidden: 'errors.forbidden',
  notFound: 'errors.notFound',
  validation: 'errors.validation',
  /** 423 Locked — the account itself is barred, not the request (HS-1225). */
  locked: 'errors.locked',
  /** 429 Too Many Requests — rate limited, retry later (HS-1225). */
  tooManyRequests: 'errors.tooManyRequests',
  server: 'errors.server',
  unknown: 'errors.unknown',
} as const;

export type ErrorMessageKey = (typeof ERROR_MESSAGE_KEYS)[keyof typeof ERROR_MESSAGE_KEYS];

const STATUS_UNAUTHORIZED = 401;
const STATUS_FORBIDDEN = 403;
const STATUS_NOT_FOUND = 404;
const STATUS_LOCKED = 423;
const STATUS_TOO_MANY_REQUESTS = 429;
const STATUS_CLIENT_MIN = 400;
const STATUS_SERVER_MIN = 500;

/** Map an HTTP status to a stable message key. */
function keyForStatus(status: number | undefined): ErrorMessageKey {
  if (status === undefined) return ERROR_MESSAGE_KEYS.network;
  if (status === STATUS_UNAUTHORIZED) return ERROR_MESSAGE_KEYS.unauthorized;
  if (status === STATUS_FORBIDDEN) return ERROR_MESSAGE_KEYS.forbidden;
  if (status === STATUS_NOT_FOUND) return ERROR_MESSAGE_KEYS.notFound;
  // 423 and 429 are the two 4xx the sign-in screens must tell apart from "your input is wrong":
  // one says the account is barred, the other says to come back later. Without them both fell
  // into `validation`, whose copy asks the user to check a field they typed correctly.
  if (status === STATUS_LOCKED) return ERROR_MESSAGE_KEYS.locked;
  if (status === STATUS_TOO_MANY_REQUESTS) return ERROR_MESSAGE_KEYS.tooManyRequests;
  if (status >= STATUS_SERVER_MIN) return ERROR_MESSAGE_KEYS.server;
  if (status >= STATUS_CLIENT_MIN) return ERROR_MESSAGE_KEYS.validation;
  return ERROR_MESSAGE_KEYS.unknown;
}

export interface NormalizedApiErrorInit {
  /** i18n key for the user-facing message. */
  messageKey: ErrorMessageKey;
  /** HTTP status, when the failure reached the server. */
  status?: number;
  /** Raw server-provided detail (envelope `error` string or ProblemDetails detail/title). */
  detail?: string;
  /** Structured validation details, usually a field → messages map. */
  details?: unknown;
  /**
   * The raw response body, verbatim. Some endpoints answer a non-2xx with a *typed* payload the
   * caller has to read — `POST /api/User/{institutionId}/{userId}/move` returns its conflict, and
   * its revocation preview, as the `data` of a 409 envelope (HS-1288). `detail`/`details`/
   * `errorCode` only ever carry the envelope's prose and its flat extension members, so without
   * this the payload the backend deliberately typed would be unreachable — and the only way back
   * to it would be `cause`, i.e. exactly the axios internals this module exists to hide.
   */
  body?: unknown;
  /**
   * Machine-readable cause, when the backend distinguishes several failures behind one status.
   * It is a ProblemDetails extension member (`unsupported_format`, `file_too_large`, …) that sits
   * beside `detail` rather than inside `details`, so callers branch on it instead of parsing prose.
   */
  errorCode?: string;
  /** The original error, preserved for logging. */
  cause?: unknown;
}

/**
 * The one error type every request rejects with. `message` carries the server
 * detail when present (useful in logs); UI code renders `t(messageKey)`.
 */
export class NormalizedApiError extends Error {
  readonly messageKey: ErrorMessageKey;
  readonly status?: number;
  readonly detail?: string;
  readonly details?: unknown;
  readonly errorCode?: string;
  readonly body?: unknown;

  constructor(init: NormalizedApiErrorInit) {
    super(init.detail ?? init.messageKey, { cause: init.cause });
    this.name = 'NormalizedApiError';
    this.messageKey = init.messageKey;
    this.status = init.status;
    this.detail = init.detail;
    this.details = init.details;
    this.errorCode = init.errorCode;
    this.body = init.body;
  }
}

/** Minimal structural view of an RFC 7807 ProblemDetails body. */
interface ProblemDetailsLike {
  title?: string | null;
  detail?: string | null;
  status?: number | null;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

/** Pull the most specific human detail out of an unknown error response body. */
function detailFromBody(body: unknown): string | undefined {
  if (typeof body === 'string') return body || undefined;
  if (isRecord(body)) {
    const problem = body as ProblemDetailsLike & { error?: unknown };
    if (typeof problem.error === 'string' && problem.error) return problem.error;
    if (typeof problem.detail === 'string' && problem.detail) return problem.detail;
    if (typeof problem.title === 'string' && problem.title) return problem.title;
  }
  return undefined;
}

function detailsFromBody(body: unknown): unknown {
  if (!isRecord(body)) return undefined;
  return body.details ?? body.errors;
}

/** Read the `errorCode` extension member, ignoring any non-string the backend might send. */
function errorCodeFromBody(body: unknown): string | undefined {
  if (!isRecord(body)) return undefined;
  return typeof body.errorCode === 'string' && body.errorCode ? body.errorCode : undefined;
}

/**
 * Normalize any thrown value into a `NormalizedApiError`. Idempotent: an existing
 * `NormalizedApiError` is returned unchanged. `status`/`body` come from the caller
 * (the axios error or the failed envelope), so this stays axios-agnostic.
 */
export function normalizeError(
  cause: unknown,
  context: { status?: number; body?: unknown; timeout?: boolean } = {},
): NormalizedApiError {
  if (cause instanceof NormalizedApiError) return cause;
  const messageKey = context.timeout ? ERROR_MESSAGE_KEYS.timeout : keyForStatus(context.status);
  return new NormalizedApiError({
    messageKey,
    status: context.status,
    detail: detailFromBody(context.body),
    details: detailsFromBody(context.body),
    errorCode: errorCodeFromBody(context.body),
    body: context.body,
    cause,
  });
}

/** Resolve any thrown value to a stable i18n message key. */
export function getErrorMessage(error: unknown): ErrorMessageKey {
  if (error instanceof NormalizedApiError) return error.messageKey;
  return ERROR_MESSAGE_KEYS.unknown;
}
